/**
 * The route table, as data.
 *
 * `router.tsx` maps each entry to a React element; the coverage tests read
 * this module alone. That split is the point: `router.tsx` eagerly imports
 * ~30 route components from the App monolith, so a test importing it would
 * drag PostHog, Convex, and every store into jsdom to answer a question
 * about strings.
 *
 * Every route declares its `kind`, so a non-screen is an explicit, typed
 * decision rather than an entry on an exceptions list somewhere else:
 *
 *   - `screen`   — a real user destination. MUST name a manifest in
 *                  `shared/app-surfaces.ts`; CI fails otherwise, which is
 *                  what stops a new screen from shipping agent-invisible.
 *   - `redirect` — bounces elsewhere and renders nothing of its own.
 *   - `special`  — renders, but isn't a destination a user navigates to:
 *                  OAuth callbacks, the chrome-less embed, the catch-all.
 */
import type { AppSurfaceId } from "@/shared/app-surfaces";

export type AppRouteEntry =
  | { path: string; kind: "screen"; surfaceId: AppSurfaceId }
  | { path: string; kind: "redirect"; note: string }
  | { path: string; kind: "special"; note: string };

export const APP_ROUTES: readonly AppRouteEntry[] = [
  {
    path: "/",
    kind: "redirect",
    note: "Index; redirects to /playground.",
  },
  {
    path: "home",
    kind: "redirect",
    note: "Legacy: the Home surface was removed; redirects to /playground.",
  },
  { path: "connectors", kind: "screen", surfaceId: "connectors" },
  {
    path: "servers",
    kind: "redirect",
    note: "Legacy: the tab was renamed Servers → Connectors; redirects to /connectors.",
  },
  {
    path: "clients",
    kind: "redirect",
    note: "Legacy: Clients merged into Connectors (one Client Connectors page); redirects to /connectors.",
  },
  // The per-client config canvas. The page only offers it for the two
  // direct-API clients, but the permalink opens any of them.
  { path: "clients/:hostId", kind: "screen", surfaceId: "connectors" },
  { path: "host-compare", kind: "screen", surfaceId: "host-compare" },
  {
    path: "embed/host-compare",
    kind: "special",
    note: "Chrome-less vanity surface (caniuse.dev): no sidebar, no NUX.",
  },
  {
    path: "capabilities/:capabilitySlug",
    kind: "screen",
    surfaceId: "host-compare",
  },
  {
    path: "hosts",
    kind: "redirect",
    note: "Legacy: the tab was renamed Hosts → Clients; redirects to /clients.",
  },
  {
    path: "hosts/:hostId",
    kind: "redirect",
    note: "Legacy deep link; re-encoded through buildHostsPath to /clients/:hostId.",
  },
  { path: "tools", kind: "screen", surfaceId: "tools" },
  { path: "resources", kind: "screen", surfaceId: "resources" },
  { path: "prompts", kind: "screen", surfaceId: "prompts" },
  { path: "tasks", kind: "screen", surfaceId: "tasks" },
  { path: "skills", kind: "screen", surfaceId: "skills" },
  { path: "oauth-flow", kind: "screen", surfaceId: "oauth-flow" },
  { path: "tracing", kind: "screen", surfaceId: "tracing" },
  // `ChatAliasRoute` is a `<Navigate replace>` — it renders nothing of its
  // own, exactly like `client-config`. `chat` survives as a nav SEGMENT
  // (normalized to `playground`), which is a separate question from whether
  // any screen renders here.
  {
    path: "chat",
    kind: "redirect",
    note: "Legacy alias; redirects to /playground.",
  },
  {
    path: "chat/*",
    kind: "redirect",
    note: "Legacy deep link; redirects to /playground so old bookmarks land there rather than the catch-all.",
  },
  { path: "playground", kind: "screen", surfaceId: "playground" },
  { path: "settings", kind: "screen", surfaceId: "settings" },
  { path: "settings/api-keys", kind: "screen", surfaceId: "settings" },
  {
    path: "client-config",
    kind: "redirect",
    note: "Legacy alias; redirects to /connectors.",
  },
  { path: "evals", kind: "screen", surfaceId: "evals" },
  { path: "evals/create", kind: "screen", surfaceId: "evals" },
  { path: "evals/suite/:suiteId", kind: "screen", surfaceId: "evals" },
  {
    path: "evals/suite/:suiteId/runs/:runId",
    kind: "screen",
    surfaceId: "evals",
  },
  {
    path: "evals/suite/:suiteId/test/:testId",
    kind: "screen",
    surfaceId: "evals",
  },
  {
    path: "evals/suite/:suiteId/test/:testId/edit",
    kind: "screen",
    surfaceId: "evals",
  },
  { path: "evals/suite/:suiteId/edit", kind: "screen", surfaceId: "evals" },
  // sphinx: runner fleet + job table (server/routes/sphinx/queue.ts).
  { path: "queue", kind: "screen", surfaceId: "queue" },
  {
    path: "callback",
    kind: "special",
    note: "Auth callback landing; renders Connectors.",
  },
  {
    path: "oauth/callback/*",
    kind: "special",
    note: "MCP server OAuth callback; renders Connectors.",
  },
  {
    path: "*",
    kind: "special",
    note: "Catch-all; renders Connectors.",
  },
] as const;
